import {
  BackBar,
  checkTransfer,
  describeError,
  dirhams,
  FINANCE_ERRORS,
  PhotoField,
  parseOperationAmount,
  present,
  today,
} from '@horof/shared';
import { Alert, App, Button, Input, Select, Skeleton } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from 'urql';
import {
  CreateTransferDocument,
  useFinanceLists,
  withBalances,
} from './finance-data';

type Direction = 'deposit' | 'withdraw';

const LABELS: Record<Direction, string> = {
  deposit: 'إيداع في البنك',
  withdraw: 'سحب من البنك',
};

/**
 * The treasurer takes cash to the bank (or takes some out): one of two big
 * choices, the amount, the date and a photo of the bank's slip. arqam writes
 * the two operations (out of one account, into the other).
 */
export function BankPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [{ data: lists }] = useFinanceLists();
  const [{ fetching }, create] = useMutation(CreateTransferDocument);
  const [direction, setDirection] = useState<Direction | null>(null);
  const [amountText, setAmountText] = useState('');
  const [date, setDate] = useState(today());
  const [bankId, setBankId] = useState<string | null>(null);
  const [slipId, setSlipId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (!lists) {
    return (
      <div className="page">
        <BackBar to="/" label="الرئيسية" />
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  const accounts = present(lists.financialAccounts?.nodes);
  const cash = accounts.find((a) => a.type === 'cash');
  const banks = accounts.filter((a) => a.type === 'bank_account');
  const bank =
    banks.find((b) => b.id === bankId) ??
    (banks.length === 1 ? banks[0] : null);
  const amount = parseOperationAmount(amountText);

  if (!cash || banks.length === 0) {
    return (
      <div className="page">
        <BackBar to="/" label="الرئيسية" />
        <Alert
          type="warning"
          showIcon
          message="لا يوجد صندوق وحساب بنكي"
          description="يُضاف الحساب البنكي من الحاسوب."
        />
      </div>
    );
  }

  const { problems, transfer } = checkTransfer({
    date,
    fromAccountId: direction === 'deposit' ? cash.id : (bank?.id ?? null),
    toAccountId: direction === 'deposit' ? (bank?.id ?? null) : cash.id,
    amount: amountText,
    description: direction ? LABELS[direction] : '',
    receiptPhotoId: slipId,
    needsReview: false,
    reviewNote: '',
  });
  const missing = [
    ...(direction ? [] : ['اختر: إيداع أو سحب.']),
    ...problems.filter((p) => !p.startsWith('اكتب البيان')),
  ];

  async function save() {
    setSubmitted(true);
    if (!direction || !transfer || missing.length) return;
    setProblem(null);
    const result = await create({ transfer }, withBalances);
    if (result.error) {
      setProblem(describeError(result.error, 'تعذّر الحفظ.', FINANCE_ERRORS));
      return;
    }
    message.success(`حُفظ: ${LABELS[direction]} ${dirhams(transfer.amount)}`);
    navigate('/', { replace: true });
  }

  return (
    <div className="page">
      <BackBar to="/" label="الرئيسية" />
      <h1 className="member-title">البنك</h1>
      {problem && <Alert type="error" showIcon message={problem} />}
      <form
        className="card operation-form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="choice-buttons is-column">
          {(['deposit', 'withdraw'] as const).map((d) => (
            <Button
              key={d}
              size="large"
              className="bank-choice"
              type={direction === d ? 'primary' : 'default'}
              onClick={() => setDirection(d)}
            >
              {LABELS[d]}
              <span className="choice-hint">
                {d === 'deposit'
                  ? `من ${cash.name} إلى البنك`
                  : `من البنك إلى ${cash.name}`}
              </span>
            </Button>
          ))}
        </div>

        {banks.length > 1 && (
          <>
            <div className="field-label">البنك</div>
            <Select
              size="large"
              placeholder="اختر الحساب البنكي"
              value={bank?.id}
              onChange={setBankId}
              options={banks.map((b) => ({ value: b.id, label: b.name }))}
            />
          </>
        )}

        <label className="field-label" htmlFor="amount">
          المبلغ (درهم)
        </label>
        <Input
          id="amount"
          size="large"
          inputMode="decimal"
          className="amount-input ltr-input"
          value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
        />
        {amount && <div className="field-hint">{dirhams(amount)}</div>}

        <label className="field-label" htmlFor="date">
          التاريخ
        </label>
        <Input
          id="date"
          type="date"
          size="large"
          className="ltr-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />

        <PhotoField
          kind="RECEIPT"
          label="صورة وصل البنك"
          hint="صوّر الوصل الذي أعطاك البنك."
          value={slipId}
          onChange={setSlipId}
          onBusyChange={setUploading}
        />

        {submitted && missing.length > 0 && (
          <ul className="field-error problems">
            {missing.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <Button
          type="primary"
          htmlType="submit"
          block
          className="big-button"
          loading={fetching}
          disabled={uploading}
        >
          حفظ
        </Button>
      </form>
    </div>
  );
}
